import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { IServiceOrganizationGroupHistory } from '@common/interfaces';
import type { ISchemaContainer } from './schemaContainer';
import { BittiumRecordingAnalyst } from '../enums/bittiumRecordingAnalyst';
import { SomnologyOrganizationType } from '../enums/somnologyOrganizationType';
import { replaceKoiError } from './replaceKoiError';
import { emailSchema } from './schemas/emailSchema';
import { zorgMailNummerSchema } from './schemas/zorgMailNummerSchema';
import { isToday } from './utils/validateIsToday';

export interface IWaitingTimeValidated {
    applicationWaitingTime: number | null;
    treatmentWaitingTime: number | null;
}

const serviceOrganizationSettingWaitingTime = Koi.object().keys({
    applicationWaitingTime: Koi.number().max(300).allow(null),
    treatmentWaitingTime: Koi.number().max(300).allow(null),
});

export const serviceOrganizationSettingWaitingTimeSchema: ISchemaContainer<IWaitingTimeValidated> = {
    schema: serviceOrganizationSettingWaitingTime.options({ convert: false, presence: 'required' }),
};

interface IServiceOrganizationSettingBasic {
    endDate: string | null;
    organizationId: string;
    serviceId: string;
    serviceOrganizationGroupHistory: IServiceOrganizationGroupHistory[];
    startDate: string;
}
export interface AssetValidation {
    isEnabled: boolean;
    type: 'BSN';
}
export interface IServiceOrganizationSettingList {
    assetValidation: AssetValidation;
    canPerformGlaucoomExamination?: boolean;
    emailAddressOrganizationInbox: string | null;
    emailAddressOrganizationInboxAlt: string | null;
    isRequestRecipient: boolean;
    ksyosHolterAnalystOrganizationId: string | null;
    optoConnectPracticeId: string | null;
    receiveZorgmailOnConsultOpen: boolean;
    recipientIsOwnOrganization: boolean;
    sendBittiumRecordingsTo: BittiumRecordingAnalyst | null;
    sendNotificationOrganizationInbox: boolean;
    serviceGraderPoolId: string | null;
    somnologyOrganizationType: SomnologyOrganizationType | null;
    zorgmailNummer: string | null;
}

export type IServiceOrganizationSettingValidated = IServiceOrganizationSettingBasic & IServiceOrganizationSettingList;

interface IServiceOrganizationSettingWithSettingsValidated extends IServiceOrganizationSettingBasic {
    settings: Partial<IServiceOrganizationSettingList>;
}

export type IServiceOrganizationSettingAddEditValidated =
    | IServiceOrganizationSettingValidated
    | IServiceOrganizationSettingWithSettingsValidated;

const serviceOrganizationSettingBasic = Koi.object().keys({
    endDate: Koi.koi().date().allow(null),
    organizationId: Koi.string().uuid(),
    serviceId: Koi.string().uuid(),
    serviceOrganizationGroupHistory: Koi.array().items(
        Koi.object({
            endDate: Koi.koi().date().endDate().allow(null),
            id: Koi.string().uuid(),
            isDeleted: Koi.boolean(),
            lastUpdatedByPractitionerId: Koi.string().uuid().allow(null),
            serviceGroupSettingId: Koi.string().uuid(),
            startDate: Koi.koi().date(),
        })
    ),
    startDate: Koi.koi().date(),
});

export const settingNames = [
    'canReferPatientWithoutSmartphone',
    'emailAddressOrganizationInbox',
    'emailAddressOrganizationInboxAlt',
    'isRequestRecipient',
    'isSendingDeviceToPatientsAddress',
    'isUsingHideRequestedExaminationTaskFeature',
    'ksyosHolterAnalystOrganizationId',
    'optoConnectPracticeId',
    'receiveZorgmailOnConsultOpen',
    'recipientIsOwnOrganization',
    'sendBittiumRecordingsTo',
    'sendNotificationOrganizationInbox',
    'serviceGraderPoolId',
    'somnologyOrganizationType',
    'zorgmailNummer',
];

const settingsSchema = Koi.object().keys({
    assetValidation: Koi.object({
        isEnabled: Koi.boolean(),
        type: Koi.string().valid('BSN'),
    }).optional(),
    canPerformGlaucoomExamination: Koi.boolean().allow(null).optional(),
    canReferPatientWithoutSmartphone: Koi.boolean(),
    emailAddressOrganizationInbox: emailSchema.allow(null),
    emailAddressOrganizationInboxAlt: emailSchema.allow(null),
    isRequestRecipient: Koi.boolean(),
    isSendingDeviceToPatientsAddress: Koi.boolean(),
    isUsingHideRequestedExaminationTaskFeature: Koi.boolean(),
    ksyosHolterAnalystOrganizationId: Koi.string().uuid().allow(null),
    optoConnectPracticeId: Koi.string().max(255).allow(null),
    receiveZorgmailOnConsultOpen: Koi.boolean(),
    recipientIsOwnOrganization: Koi.boolean(),
    sendBittiumRecordingsTo: Koi.koi().enum(BittiumRecordingAnalyst).allow(null),
    sendNotificationOrganizationInbox: Koi.boolean(),
    serviceGraderPoolId: Koi.string().uuid().allow(null),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType).allow(null),
    zorgmailNummer: Koi.when('receiveZorgmailOnConsultOpen', {
        is: true,
        otherwise: zorgMailNummerSchema.allow(null),
        then: zorgMailNummerSchema,
    }),
});

const serviceOrganizationSettingAddEdit = serviceOrganizationSettingBasic.concat(settingsSchema);

export const serviceOrganizationSettingAddEditSchema: ISchemaContainer<IServiceOrganizationSettingAddEditValidated> = {
    schema: serviceOrganizationSettingAddEdit,
};

export interface IUpdateEndDateValidated {
    endDate: string;
    serviceOrganizationSettingIds: string[];
    startDate: string;
}

export const serviceOrganizationSettingUpdateEndDateSchema: ISchemaContainer<IUpdateEndDateValidated> = {
    schema: Koi.object()
        .keys({
            endDate: Koi.koi()
                .date()
                .custom(isToday)
                .message('Datum moet vandaag zijn')
                .error((errors: Joi.ErrorReport[]) => replaceKoiError(errors, 'any.custom', 'koi_isNotToday')),
            serviceOrganizationSettingIds: Koi.array().items(Koi.string().uuid()).min(1),
            startDate: Koi.koi().date(),
        })
        .options({ convert: false, presence: 'required' }),
};
